import {Injectable} from '@angular/core';
import {BaseService} from '../services';
import {PaginationModel, QuoteOrderSampleModel, RequestParamModel} from '../models';
import {catchError, finalize, lastValueFrom, map, Observable, share, throwError} from 'rxjs';
import {Constants} from '../constants';
import {QuoteOrderSampleStatusModel} from '../models/quote-order-sample-status-model';

@Injectable({
  providedIn: 'root'
})
export class QuoteService {

  // Track ongoing Unleashed syncs per quote ID to prevent race conditions
  private ongoingSyncs = new Map<number, Promise<any>>();

  constructor(private baseService: BaseService) {
  }

  getQuotes(requestParamModel: RequestParamModel, skipLoader: boolean = false): any {
    return this.baseService.get('quote', requestParamModel, skipLoader)
      .pipe(map<any, any>(response => {
          const accountList = new PaginationModel<any>();
          const headers = response.headers;
          accountList.setHeaders(headers);
          accountList.data = response.body.data as any[];
          return accountList;
        }),
        catchError(err => throwError(() => err))
      );
  }

  /**
   * Same rule as the Discontinued badge in product-detail-section: is_deleted, except Custom
   * Products — the Master SKU import soft-deletes those on every run, which is not a discontinuation.
   */
  static isDiscontinuedLine(line: any): boolean {
    return line?.product?.is_deleted == Constants.PRODUCT_DELETED_YES && !line?.product?.is_custom_product;
  }

  /** Labels of the discontinued lines, as the product table shows them: "CATALINA (6PA216298/5-17)". */
  static discontinuedLineLabels(lines: any[] | undefined): string[] {
    return [...new Set((lines || [])
      .filter((line: any) => QuoteService.isDiscontinuedLine(line))
      .map((line: any) => {
        const name = line.consumables_prices?.sku_code || line.product?.family?.name;
        const sku = line.product?.sku;
        return name && sku ? `${name} (${sku})` : (name || sku || '');
      })
      .filter(Boolean))];
  }

  /**
   * Re-reads a quote's line items so a page loaded before a product was discontinued cannot
   * act on stale data. If the fetch fails the caller's loaded lines are used; api-v2 enforces
   * the same rule on accept and on payment, so the server still has the last word.
   */
  async getFreshQuoteProducts(quoteId: number, loadedLines: any[] | undefined): Promise<any[]> {
    try {
      const params = new RequestParamModel();
      params.expands = ['quote_product', 'quote_product.product', 'quote_product.product.family'];
      const fresh: any = await lastValueFrom(this.getQuoteDetail(quoteId, params, false, true));
      if (Array.isArray(fresh?.quote_product)) {
        return fresh.quote_product;
      }
    } catch {
      // fall back to the loaded lines
    }
    return loadedLines || [];
  }

  getQuoteDetail(id: number, requestParamModel: RequestParamModel, skipLoader: boolean = false, skipError: boolean = false) {
    return this.baseService.get(`quote/${id}`, requestParamModel, skipLoader, skipError)
      .pipe(map<any, any>(response => {
        return response.body.data;
      }), catchError(err => throwError(() => err)));
  }

  deleteQuote(id: any) {
    return this.baseService.delete(`quote/${id}`)
      .pipe(map<any, number>(response => {
        return response;
      }), catchError(err => throwError(() => err)));
  }

  createQuote(quote: any, skipLoader: boolean = false) {
    return this.baseService.post('quote', quote, skipLoader)
      .pipe(map<any, number>(response => {
        return response.data;
      }), catchError(err => throwError(() => err)));
  }

  /**
   * "Create a quote from this request": api-v2 raises a DRAFT quote from the sample
   * request and converts the request to it in one call. Answers `{id, quote_number}`.
   */
  createQuoteFromSampleRequest(sampleRequestId: any) {
    return this.baseService.post(`quote/create-quote-from-sample-request/${sampleRequestId}`, {})
      .pipe(map<any, any>(response => response.data), catchError(err => throwError(() => err)));
  }

  updateQuote(quote: any) {
    let id = quote.id;
    delete quote.id;
    return this.baseService.patch(`quote/${id}`, quote)
      .pipe(map<any, number>(response => {
        return response.data;
      }), catchError(err => throwError(() => err)));
  }

  getQuoteOrderSampleStatus(requestParamModel: RequestParamModel): any {
    return this.baseService.get('status', requestParamModel)
      .pipe(map<any, any>(response => {
          const quoteOrderSampleStatusList = new PaginationModel<QuoteOrderSampleStatusModel>();
          const headers = response.headers;
          quoteOrderSampleStatusList.setHeaders(headers);
          quoteOrderSampleStatusList.data = response.body.data as QuoteOrderSampleStatusModel[];
          return quoteOrderSampleStatusList;
        }),
        catchError(err => throwError(() => err))
      );
    ;
  }

  createOrUpdateQuoteDeliveryAddress(deliveryPayload: any, id?: number, skipLoader: boolean = false) {
    const request$ = id
      ? this.baseService.patch(`quote-address/update/${id}`, deliveryPayload, skipLoader)
      : this.baseService.post('quote-address/create', deliveryPayload, skipLoader);
    return request$.pipe(map<any, number>(response => {
      return response.data;
    }), catchError(err => throwError(() => err)));
  }

  createOrUpdateQuoteDeliveryAddressForCreateClone(deliveryPayload: any, id?: number, skipLoader: boolean = false) {
    const request$ = id
      ? this.baseService.patch(`quote-address/${id}`, deliveryPayload, skipLoader)
      : this.baseService.post('quote-address', deliveryPayload, skipLoader);
    return request$.pipe(map<any, number>(response => {
      return response.data;
    }), catchError(err => throwError(() => err)));
  }

  saveOrUpdateQuoteSiteContact(quoteSiteContactData: any, id?: number, skipLoader: boolean = false) {
    const request$ = id
      ? this.baseService.patch(`quote-site-contact/${id}`, quoteSiteContactData, skipLoader)
      : this.baseService.post('quote-site-contact', quoteSiteContactData, skipLoader);

    return request$.pipe(
      map<any, number>(response => response.data),
      catchError(err => throwError(() => err))
    );
  }

  deleteQuoteSiteContact(id: number) {
    return this.baseService.delete(`quote-site-contact/${id}`);
  }

  deleteQuoteAddress(id: number) {
    return this.baseService.delete(`quote-address/${id}`);
  }

  deleteJoinQuoteSiteContactScheduleDelivery(id: number) {
    return this.baseService.delete(`join-quote-site-contact-schedule-delivery/${id}`);
  }

  quoteSummary(quoteId, skipLoader: boolean = false) {
    return this.baseService.post('quote-summary', quoteId, skipLoader)
      .pipe(map<any, number>(response => {
        return response.data;
      }), catchError(err => throwError(() => err)));
  }

  updateQuoteSummary(data: any, id: any): Observable<any> {
    return this.baseService.patch(`quote-summary/${id}`, data)
      .pipe(map<any, any>(response => {
        return response.data;
      }), catchError(err => throwError(() => err)));
  }

  syncDeliveryWithUnleashed(data: any, id: any): Observable<any> {
    return this.baseService.patch(`quote/sync-delivery-with-unleashed/${id}`, data)
      .pipe(map<any, any>(response => {
        return response.data;
      }), catchError(err => throwError(() => err)));
  }

  syncProductWithUnleashed(data: any, id: any): Observable<any> {
    // ✅ GUARD: Check if sync is already in progress for this quote
    const existingPromise = this.ongoingSyncs.get(id);
    if (existingPromise) {
      // Convert existing promise back to observable for the caller
      return new Observable(observer => {
        existingPromise.then(
          result => {
            observer.next(result);
            observer.complete();
          },
          error => observer.error(error)
        );
      });
    }

    // Create a promise from the observable
    const syncPromise = new Promise<any>((resolve, reject) => {
      this.baseService.patch(`quote/sync-product-with-unleashed/${id}`, data)
        .pipe(
          map<any, any>(response => {
            return response.data;
          }),
          catchError(err => {
            console.error(`🚨 [QuoteService] Sync failed for quote ${id}`, err);
            return throwError(() => err);
          })
        )
        .subscribe({
          next: (result) => {
            this.ongoingSyncs.delete(id);
            resolve(result);
          },
          error: (err) => {
            this.ongoingSyncs.delete(id);
            reject(err);
          }
        });
    });

    // Track this sync promise
    this.ongoingSyncs.set(id, syncPromise);

    // Return as observable for compatibility
    return new Observable(observer => {
      syncPromise.then(
        result => {
          observer.next(result);
          observer.complete();
        },
        error => observer.error(error)
      );
    });
  }

  syncOrderWithUnleashed(data: any, id: any): Observable<any> {
    return this.baseService.patch(`quote/sync-order-with-unleashed/${id}`, data)
      .pipe(map<any, any>(response => {
        return response.data;
      }), catchError(err => throwError(() => err)));
  }

  syncOrderFromUnleashed(id: any): Observable<any> {
    return this.baseService.patch(`quote/sync-order-from-unleashed/${id}`, null)
      .pipe(map<any, any>(response => {
        return response.data;
      }), catchError(err => throwError(() => err)));
  }

  syncAdditionalDeliveryWithUnleashed(data: any, id: any): Observable<any> {
    return this.baseService.patch(`quote/sync-additional-delivery-with-unleashed/${id}`, data)
      .pipe(map<any, any>(response => {
        return response.data;
      }), catchError(err => throwError(() => err)));
  }

  getQuoteProducts(requestParamModel: RequestParamModel): any {
    return this.baseService.get('quote-product', requestParamModel)
      .pipe(map((response: any) => {
        const quoteProducts = new PaginationModel();
        const headers = response.headers;
        quoteProducts.setHeaders(headers);
        quoteProducts.data = response.body.data;
        return quoteProducts;
      }), catchError(err => throwError(() => err)));
  }

  deleteQuoteProduct(id: any) {
    return this.baseService.delete(`quote-product/delete/${id}`);
  }

  deleteQuoteProductForCreateClone(id: any) {
    return this.baseService.delete(`quote-product/${id}`);
  }

  updateQuoteProduct(data: any, productId: any, skipLoader: boolean = false): Observable<any> {
    // return this.baseService.patch(`quote-product/${productId}`, data, skipLoader)
    return this.baseService.patch(`quote-product/update/${productId}`, data, skipLoader)
      .pipe(map<any, any>(response => {
        return response.data;
      }), catchError(err => throwError(() => err)));
  }

  updateQuoteProductForCreateClone(data: any, productId: any, skipLoader: boolean = false): Observable<any> {
    // return this.baseService.patch(`quote-product/${productId}`, data, skipLoader)
    return this.baseService.patch(`quote-product/${productId}`, data, skipLoader)
      .pipe(map<any, any>(response => {
        return response.data;
      }), catchError(err => throwError(() => err)));
  }

  createQuoteProduct(data: any, skipLoader: boolean = false): Observable<any> {
    // return this.baseService.post('quote-product', data, skipLoader)
    return this.baseService.post('quote-product/create', data, skipLoader)
      .pipe(map<any, any>(response => {
        return response.data;
      }), catchError(err => throwError(() => err)));
  }

  // Returns { children, lines } — `children` is every mergeable (non-root) line of this product on
  // this order, each carrying the ids it may be merged into; `lines` is every line including the
  // roots, so the target picker can label and price them.
  getSplitChildren(quoteId: number, productId: number, skipLoader: boolean = false): Observable<any> {
    return this.baseService.get(`quote-product/split-children?quote_id=${quoteId}&product_id=${productId}`, undefined, skipLoader)
      .pipe(map<any, any>(response => {
        const data = response.body?.data ?? response.data ?? {};
        // Tolerate the pre-target-picker shape (a bare array of children).
        return Array.isArray(data) ? {children: data, lines: data} : {children: data.children ?? [], lines: data.lines ?? []};
      }), catchError(err => throwError(() => err)));
  }

  // What merging one line into another would do to consumables, before committing to it.
  getMergePreview(sourceId: number, targetId: number, skipLoader: boolean = false): Observable<any> {
    return this.baseService.get(`quote-product/merge-preview?source_id=${sourceId}&target_id=${targetId}`, undefined, skipLoader)
      .pipe(map<any, any>(response => response.body?.data ?? response.data ?? null),
        catchError(err => throwError(() => err)));
  }

  // `targetId` merges the line into any other line of the same split tree. Omit it to merge into
  // the line it was split from (the server's default). `applyMismatchedConsumables` answers the
  // case where only one of the two lines carries a consumable.
  revertSplitQuoteProduct(
    childId: number,
    targetId?: number | null,
    applyMismatchedConsumables?: boolean,
    skipLoader: boolean = false
  ): Observable<any> {
    const payload: any = targetId ? {target_id: targetId} : {};
    if (applyMismatchedConsumables !== undefined) {
      payload.apply_mismatched_consumables = applyMismatchedConsumables;
    }
    return this.baseService.post(`quote-product/revert-split/${childId}`, payload, skipLoader)
      .pipe(map<any, any>(response => {
        return response.data;
      }), catchError(err => throwError(() => err)));
  }

  // Atomic split: one server call creates the child AND reduces the parent, gated on a
  // successful Unleashed push/verify (server compensates on failure → order left unchanged).
  splitProductLine(payload: any, skipLoader: boolean = false): Observable<any> {
    return this.baseService.post(`quote-product/split/${payload.parent_id}`, payload, skipLoader)
      .pipe(map<any, any>(response => {
        return response.data;
      }), catchError(err => throwError(() => err)));
  }

  createQuoteProductForCreateClone(data: any, skipLoader: boolean = false): Observable<any> {
    // return this.baseService.post('quote-product', data, skipLoader)
    return this.baseService.post('quote-product', data, skipLoader)
      .pipe(map<any, any>(response => {
        return response.data;
      }), catchError(err => throwError(() => err)));
  }

  /** Update-only variant of {@link addUpdateLogisticDetails} — same rule:
   *  `orderModel.id` is `quote_logistic.id`, never the quote's id. */
  UpdateLogisticDetails(orderModel: any): Observable<number> {
    return this.baseService.patch('quote-logistic/' + orderModel.id, orderModel)
      .pipe(map<any, number>(response => {
          orderModel = response.data as QuoteOrderSampleModel;
          return orderModel.id;
        }),
        catchError(err => throwError(() => err))
      );
  }

  /**
   * Create or update a record's logistic row (carrier, delivery charge, packer,
   * primary tracking number, packing box).
   *
   * **`orderModel.id` is `quote_logistic.id`, NOT the quote's id.** The two
   * tables are separate — `quote_logistic` has its own primary key and a unique
   * `quote_id` — so read it off the expanded record
   * (`modelOrder.quote_logistic?.id`) and pass the quote's id as `quote_id`.
   * With a quote id in `id` this PATCHes a row that does not exist and api-v2
   * answers `404 quote_logistic does not exist`.
   *
   * Leave `id` undefined when the record has no logistic row yet: that routes
   * the write to POST, which upserts on `quote_id`.
   */
  addUpdateLogisticDetails(orderModel: any): Observable<number> {
    return this.saveLogisticDetails(orderModel).pipe(
      map<any, number>(saved => (saved as QuoteOrderSampleModel)?.id)
    );
  }

  /**
   * {@link addUpdateLogisticDetails}, answering the saved row itself: a box or
   * bags change on a sample request carries `unleashed_sync_started` (and
   * `unleashed_sync` when its Unleashed order is known to be locked).
   */
  saveLogisticDetails(orderModel: any): Observable<any> {
    const url = 'quote-logistic';
    const id = orderModel.id;
    if (id) {
      delete orderModel.id;
    }
    const request$ = id
      ? this.baseService.patch(`${url}/${id}`, orderModel)
      : this.baseService.post(url, orderModel);

    return request$.pipe(
      map<any, any>(response => response.data),
      catchError(err => throwError(() => err))
    );
  }

  /**
   * How a sample request's Unleashed sync went — the one a product, packing or
   * stage write queued; `since` is that write's `unleashed_sync_started`.
   * api-v2 waits for it if it is still running. No loader and no error toast:
   * the page stays usable while Unleashed answers.
   *
   * @return `{order_number, order_status, editable, synced}`, or null when the
   *         request has no Unleashed order.
   */
  getSampleUnleashedSync(quoteId: number, since: string): Observable<any> {
    return this.baseService.get(`quote/sample-unleashed-sync/${quoteId}?since=${encodeURIComponent(since)}`, undefined, true, true)
      .pipe(map<any, any>(response => response?.body?.data?.unleashed_sync ?? null));
  }

  updateStockRequiredDate(orderModel: any): Observable<number> {
    let id = orderModel.id;
    delete orderModel.id;
    return this.baseService.patch('quote/update-stock-required-date/' + id, orderModel)
      .pipe(map<any, number>(response => {
          orderModel = response.data as QuoteOrderSampleModel;
          return orderModel.id;
        }),
        catchError(err => throwError(() => err))
      );
  }

  validateSRD(quoteId: number): Observable<any> {
    return this.baseService.post(`quote/validate-srd/${quoteId}`, {})
      .pipe(
        // Return full response to access both data.isValid and data.message
        catchError(err => throwError(() => err))
      );
  }

  updateStorageCommencementDate(orderModel: any): Observable<any> {
    const id = orderModel.id;
    const payload = { ...orderModel };
    delete payload.id;
    return this.baseService.patch(`quote/update-storage-commencement-date/${id}`, payload)
      .pipe(map<any, any>(response => response.body?.data || response),
        catchError(err => throwError(() => err))
      );
  }

  // GC-339 Phase 1: enable/disable the admin-fixed (manual) storage fee.
  // payload: { is_manual_storage_fee, manual_storage_fee_amount?, note? }
  updateManualStorageFee(orderModel: any): Observable<any> {
    const id = orderModel.id;
    const payload = { ...orderModel };
    delete payload.id;
    return this.baseService.patch(`quote/update-manual-storage-fee/${id}`, payload)
      .pipe(map<any, any>(response => response.body?.data || response),
        catchError(err => throwError(() => err))
      );
  }

  getManualStorageFeeHistory(quoteId: number, requestParamModel: RequestParamModel): Observable<PaginationModel<any>> {
    return this.baseService.get(`quote/${quoteId}/manual-storage-fee-history`, requestParamModel)
      .pipe(map<any, any>(response => {
        const list = new PaginationModel<any>();
        list.setHeaders(response.headers);
        list.data = response.body.data;
        return list;
      }), catchError(err => throwError(() => err)));
  }

  getDateHistory(quoteId: number, type: string, requestParamModel: RequestParamModel): Observable<PaginationModel<any>> {
    requestParamModel.defaultFilter = '';
    requestParamModel.addDefaultFilters('type', type, 'number');
    return this.baseService.get(`quote/${quoteId}/date-history`, requestParamModel)
      .pipe(map<any, any>(response => {
        const list = new PaginationModel<any>();
        list.setHeaders(response.headers);
        list.data = response.body.data;
        return list;
      }), catchError(err => throwError(() => err)));
  }

  getProductDateHistory(productId: number, type: string, requestParamModel: RequestParamModel): Observable<PaginationModel<any>> {
    requestParamModel.defaultFilter = '';
    requestParamModel.addDefaultFilters('type', type, 'number');
    return this.baseService.get(`quote-schedule-delivery-product/${productId}/date-history`, requestParamModel)
      .pipe(map<any, any>(response => {
        const list = new PaginationModel<any>();
        list.setHeaders(response.headers);
        list.data = response.body.data;
        return list;
      }), catchError(err => throwError(() => err)));
  }

  deleteLinkedQuoteOrder(_id: any, skipLoader: boolean = false) {
    return this.baseService.delete('link-quote-order/' + _id, skipLoader)
      .pipe(map<any, number>(response => {
          return response;
        }),
        catchError(err => throwError(() => err))
      );
  }

  reactivateQuote(id, requestData: any) {
    return this.baseService.patch(`quote/reactivate-quote/${id}`, requestData)
      .pipe(map<any, any>(response => {
          return response.data as any;
        }),
        catchError(err => throwError(() => err))
      );
  }

  createQuotePayment(requestData: any) {
    return this.baseService.post('quote-payment', requestData)
      .pipe(map<any, any>(response => {
        return response.data;
      }), catchError(err => throwError(() => err)));
  }

  splitProduction(orderModel: any): Observable<number> {
    return this.baseService.patch('quote/split-production/' + orderModel.id, null)
      .pipe(map<any, number>(response => {
          orderModel = response.data as any;
          return orderModel.id;
        }),
        catchError(err => throwError(() => err))
      );
  }

  getQuoteScheduleDeliveries(requestParamModel: RequestParamModel): any {
    return this.baseService.get('quote-schedule-delivery', requestParamModel)
      .pipe(map<any, any>(response => {
          const scheduleDeliveryList = new PaginationModel<any>();
          const headers = response.headers;
          scheduleDeliveryList.setHeaders(headers);
          scheduleDeliveryList.data = response.body.data as any[];
          return scheduleDeliveryList;
        }),
        catchError(err => throwError(() => err))
      );
  }

  getQuoteScheduleDeliveryDetail(id: number, requestParamModel: RequestParamModel) {
    return this.baseService.get(`quote-schedule-delivery/${id}`, requestParamModel)
      .pipe(map<any, any>(response => {
        return response.body.data;
      }), catchError(err => throwError(() => err)));
  }

  quoteScheduleDelivery(requestData: any, id?: any) {
    if (id) {
      return this.baseService.patch('quote-schedule-delivery/' + id, requestData)
        .pipe(map<any, any>(response => {
          let quoteScheduleDeliveryModel = response.data as any;
          return quoteScheduleDeliveryModel;
        }), catchError((err: any) => {
          return throwError(() => err);
        }));
    } else {
      return this.baseService.post('quote-schedule-delivery', requestData)
        .pipe(map<any, any>(response => {
          let quoteScheduleDeliveryModel = response.data as any;
          return quoteScheduleDeliveryModel;
        }), catchError((err: any) => {
          return throwError(() => err);
        }));
    }
  }

  quoteScheduleDeliveryProduct(requestData: any) {
    return this.baseService.post('quote-schedule-delivery-product', requestData)
      .pipe(map<any, any>(response => {
        let quoteScheduleDeliveryModel = response.data as any;
        return quoteScheduleDeliveryModel.id;
      }), catchError((err: any) => {
        return throwError(() => err);
      }));
  }

  quoteBulkScheduleDeliveryProduct(requestData: any) {
    return this.baseService.post('quote-schedule-delivery-product/bulk-create', requestData)
      .pipe(map<any, any>(response => {
        return response.data as any;
      }), catchError((err: any) => {
        return throwError(() => err);
      }));
  }

  saveQuote(quote: any) {
    let id = quote.id;
    delete quote.id;
    if (id) {
      return this.baseService.patch(`quote/${id}`, quote)
        .pipe(map<any, number>(response => {
          return response.data;
        }), catchError(err => throwError(() => err)));
    } else {
      return this.baseService.post('quote', quote)
        .pipe(map<any, number>(response => {
          return response.data;
        }), catchError(err => throwError(() => err)));
    }
  }

  splitDelivery(quoteId: any): Observable<number> {
    return this.baseService.patch('quote/split-delivery/' + quoteId, {})
      .pipe(map<any, number>(response => {
          return response.data;
        }),
        catchError(err => throwError(() => err))
      );
  }

  updateSplitDelivery(id: any, payload: any) {
    return this.baseService.patch('quote/split-delivery/by-quote-product/' + id, payload)
      .pipe(map<any, number>(response => {
          return response.data;
        }),
        catchError(err => throwError(() => err))
      );
  }

  bulkSplitDelivery(id: any, payload: any) {
    return this.baseService.patch('quote/split-delivery/bulk/' + id, payload)
      .pipe(map<any, number>(response => {
          return response.data;
        }),
        catchError(err => throwError(() => err))
      );
  }

  updateOrderStatus(data: any, orderId: any): Observable<any> {
    return this.baseService.patch(`quote/update-order-status/${orderId}`, data)
      .pipe(map<any, any>(response => {
        return response.data;
      }), catchError(err => throwError(() => err)));
  }

  generateCrateLabel(scheduleDeliveryId) {
    return this.baseService.get(`quote-schedule-delivery/generate-crate-label/${scheduleDeliveryId}`)
      .pipe(map((response: any) => {
        return response.body.data;
      }), catchError(err => throwError(() => err)));
  }

  exportTrackingLinkPDF(id: number, formData: any) {
    return this.baseService.uploadPost('quote/export-by-tracking-id/' + id, formData).pipe(map<any, any>(response => {
        return response.data as any;
      }),
      catchError(err => throwError(() => err))
    );
  }

  depositOverPaidAmountAsCredit(requestData: any) {
    return this.baseService.post('quote-order/deposit-over-paid-amount-as-credit', requestData)
      .pipe(map<any, any>(response => {
        return response.data as any;
      }), catchError((err: any) => {
        return throwError(() => err);
      }));
  }

  getOrderCounts(requestParamModel: RequestParamModel): any {
      return this.baseService.get('quote/total-by-order-counter', requestParamModel)
        .pipe(map<any, any>(response => {
          const data = response.body.data;
          return data;
        }),
        catchError(err => throwError(() => err))
    );
    }

  updateStorageFeeApplicable(data: any, orderId: any): Observable<any> {
    return this.baseService.patch(`quote/update-storage-fee-applicable/${orderId}`, data)
      .pipe(map<any, any>(response => {
        return response.data;
      }), catchError(err => throwError(() => err)));
  }
}
